<template>
  <div>
    <div ref="alert" v-show="alertMessage" :class="'alert alert-' + this.alertType + ' alert-dismissible fade show'" role="alert">
      {{ alertMessage }}
    </div>

    <order-status-form
      ref="orderStatusForm"
      :email.sync="email"
      :code.sync="code"
      :submit-disabled="submitOrderStatusFormDisabled"
      @submit="getOrderInfo"
      @reset-alert="resetAlert"
      @error="responseError"
    />

    <div ref="orders" class="card my-5" v-if="orders.length">
      <div class="table-responsive">
        <table class="table orders-statuses-table">
          <thead>
          <tr>
            <th>{{ $t('support.order') }}</th>
            <th>{{ $t('support.status') }}</th>
            <th>{{ $t('support.date') }}</th>
            <th>{{ $t('support.total_paid') }}</th>
            <th>&nbsp;</th>
          </tr>
          </thead>
          <tbody>

          <template v-if="orders.length==1">
            <tr>
              <td>{{ orders[0].number }}</td>
              <td>{{ $t('support.status.' + orders[0].status) }}</td>
              <td>{{ orders[0].created_at }}</td>
              <td>{{ orders[0].total_paid }}</td>
              <td>&nbsp;</td>
            </tr>
            <order-detail :order="orders[0]" :is-active="true"
                          @editAddressClick="openAddressForm"
                          @cancelOrderClick="cancelOrder"
                          @edit-phone="openPhoneForm"
            />
          </template>
          <template v-else v-for="order in orders">
            <tr @click="setActive(order)" style="cursor: pointer" :class="{'highlight-row': activeOrder && activeOrder.number == order.number}">
              <td>{{ order.number }}</td>
              <td>{{ $t('support.status.' + order.status) }}</td>
              <td>{{ order.created_at }}</td>
              <td>{{ order.total_paid }}</td>
              <td>
                <button class="btn btn-info">
                  {{ $t('support.order.view') }}
                </button>
              </td>
            </tr>
            <order-detail
              :order="order"
              :key="order.number"
              :is-active="activeOrder && activeOrder.number == order.number"
              @editAddressClick="openAddressForm"
              @cancelOrderClick="cancelOrder"
              @edit-phone="openPhoneForm"
            />
          </template>
          </tbody>
        </table>
      </div>
    </div>

    <change-order-address
      v-if="editingAddressOrder"
      :order="editingAddressOrder"
      @cancelEdit="editingAddressOrder=null"
      @submit="saveOrderAddress"
    />

    <change-order-phone
      v-if="editingPhoneOrder"
      :order="editingPhoneOrder"
      @submit="saveOrderPhone"
      @cancelEdit="editingPhoneOrder = null"
    />

  </div>
</template>

<script>
import Cleave from 'cleave.js';
import ChangeOrderAddress from "./ChangeOrderAddress";
import ChangeOrderPhone from "./ChangeOrderPhone";
import OrderStatusForm from "./OrderStatusForm";

export default {
  name: "OrderStatus",
  components: {OrderStatusForm, ChangeOrderAddress, ChangeOrderPhone},
  props: ['supportCode', 'orderEmail', 'country'],
  data() {
    return {
      email: this.orderEmail,
      code: this.supportCode,
      codeRequired: true,
      alertType: '',
      alertMessage: '',
      showForm: true,
      orders: [],
      activeOrder: null,
      submitOrderStatusFormDisabled: false,
      editingAddressOrder: null,
      editingPhoneOrder: null
    }
  },
  provide() {
    return {
      countryCode: this.country
    }
  },

  methods: {
    getDialCode(countryCodePhoneField) {
      const allCountries = window.intlTelInputGlobals.getCountryData();
      const phoneCountryCode = countryCodePhoneField;
      const country = allCountries.filter(item => item.iso2 === phoneCountryCode).shift();

      return country ? country.dialCode : '1';
    },

    setActive(order) {

      this.editingAddressOrder = null
      this.activeOrder = (this.activeOrder && this.activeOrder.number == order.number) ? null : order;
      this.alertMessage = '';
      this.alertType = '';
    },

    responseError(response) {
      this.alertMessage = response.status === 0 ? response.message : this.$t('support.error');
      this.alertType = 'danger';

      window.scrollTo({
        top: this.$refs.alert.scrollTop,
        behavior: 'smooth'
      })
    },

    async cancelOrder(order) {
      const confirmed = confirm(this.$t('support.confirm.order.cancel'));
      if (!confirmed) {
        return false;
      }
      const data = {
        number: order.number,
        email: this.email,
        code: this.code
      }

      const response = await fetch('/cancel-order', {
        method: 'post',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(data)
      }).then(resp => {
        return resp.json();
      });

      if (response.status != 1) {
        this.responseError(response)
      } else {
        this.alertMessage = response.message;
        this.alertType = 'success';
        for (let key in this.orders) {
          if (this.orders[key].number == response.order.number) {
            this.orders[key].status = response.order.status;
            this.orders[key].isNotExportedOrder = false;
            this.activeOrder.isNotExportedOrder = false;
            break;
          }
        }
      }

    },

    openAddressForm(order) {
      this.resetAlert();
      js_data.countries = order.countries;

      this.editingAddressOrder = order;
    },

    openPhoneForm(order) {
      this.editingPhoneOrder = order;
    },

    async saveOrderAddress(data) {
      const confirmed = confirm(this.$t('support.confirm.address_change'));
      if (!confirmed) {
        return false;
      }
      data.number = this.editingAddressOrder.number;
      data.email = this.email;
      data.code = this.code;

      this.editingAddressOrder = null;
      const response = await fetch('/change-order-address', {
        method: 'post',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(data)
      }).then(resp => {
        return resp.json();
      });

      if (response.status != 1) {
        this.responseError(response);
      } else {
        this.alertMessage = response.message;
        this.alertType = 'success';
        for (let key in this.orders) {
          if (this.orders[key].number == response.order.number) {
            this.orders[key] = response.order;
            this.activeOrder = response.order;
            break;
          }
        }
      }

    },

    async saveOrderPhone({customer_phone, countryCodePhoneField}) {
      const confirmed = confirm(this.$t('support.confirm.phone_change'));
      if (!confirmed) {
        return false;
      }

      const response = await fetch('/change-order-phone', {
        method: 'post',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          contact: {
            phone: {
              number: customer_phone,
              country_code: this.getDialCode(countryCodePhoneField)
            }
          },
          number: this.editingPhoneOrder.number,
          code: this.code,
          email: this.email
        })
      }).then(resp => {
        return resp.json();
      });

      this.editingPhoneOrder = null;

      if (response.status != 1) {
        this.responseError(response);
      } else {
        this.alertMessage = response.message;
        this.alertType = 'success';
        for (let key in this.orders) {
          if (this.orders[key].number == response.order.number) {
            this.orders[key] = response.order;
            this.activeOrder = response.order;
            break;
          }
        }
      }
    },

    async getOrderInfo() {

      if (!this.submitOrderStatusFormDisabled && !this.$refs.orderStatusForm.validate()) {
        return;
      }

      this.orders = [];
      this.resetAlert();

      this.submitOrderStatusFormDisabled = true;

      const response = await fetch('/get-order-info', {
        method: 'post',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          email: this.email,
          code: this.code
        }),
      }).then(resp => {
        return resp.json();
      });

      this.submitOrderStatusFormDisabled = false;

      if (response.status != 1) {
        this.responseError(response);
      } else {
        this.orders = response.orders
        if (this.orders.length == 1) {
          this.activeOrder = this.orders[0];
        }

        this.$nextTick(() => {
          window.scrollTo({
            top: this.$refs.orders.scrollTop + 250,
            behavior: 'smooth'
          });
        });
      }
    },
    resetAlert(){
      this.alertMessage = "";
      this.alertType = "";
    }
  },
  mounted() {
    if (this.code && this.email) {
      this.getOrderInfo();
    }
  }
}
</script>

<style scoped>

</style>
